import { Divider, Grid, Theme, Typography } from '@mui/material';
import { PhotoGroupHeading } from '@/components/letter/PhotoGroupHeading';
import { PhotoStatusItemDTO } from '@/types/photostatus/PhotoStatusItemDTO';
import { PhotoStatusPhotoList } from '@/components/letter/photoStatus/PhotoStatusPhotoList';
import { SectionCountLabel } from '@/components/common/SectionCountLabel';
import React, { FC, Fragment, ReactElement } from 'react';

interface Props {
  photos: PhotoStatusItemDTO[];
  /**
   * Opens one photo full screen. The whole list is handed over in the order the groups draw it, so
   * the view can be paged along it without closing.
   */
  onPhotoClick: (photos: PhotoStatusItemDTO[], index: number) => void;
}

/**
 * The photos of a letter with the verdict on each, as every public page that reports them draws
 * them: the token-gated «Статус фотографий» page and the «Проверка статуса письма» page.
 * <p>
 * Both pages show a sender what censorship did with their photos, so they read as one screen
 * rather than two: the same label, the same groups and the same tiles. Neither is given more than a
 * photo number, a verdict and a preview.
 */
export const PhotoStatusPhotoSection: FC<Props> = (props: Props): ReactElement => {
  const { photos, onPhotoClick } = props;

  const declinedPhotos: PhotoStatusItemDTO[] = photos.filter((photo: PhotoStatusItemDTO): boolean => photo.declined);
  const deliveredPhotos: PhotoStatusItemDTO[] = photos.filter((photo: PhotoStatusItemDTO): boolean => !photo.declined);

  // The order the tiles are drawn in, which is the order the full-screen view pages along.
  const orderedPhotos: PhotoStatusItemDTO[] = [...deliveredPhotos, ...declinedPhotos];

  /**
   * Draws one verdict group: the heading naming it and the tiles of the photos it holds.
   *
   * @param declined Whether the group holds the declined photos, which its dot says.
   * @param title The verdict.
   * @param groupPhotos The photos of the group.
   * @returns The group.
   */
  const renderGroup = (declined: boolean, title: string, groupPhotos: PhotoStatusItemDTO[]): ReactElement => (
    <Grid item={true}>
      <Grid container={true} direction={'column'} spacing={1.5}>
        <Grid item={true}>
          <PhotoGroupHeading declined={declined} title={title} count={`${groupPhotos.length} шт`} />
        </Grid>
        <Grid item={true}>
          <PhotoStatusPhotoList
            photos={groupPhotos}
            onPhotoClick={(photo: PhotoStatusItemDTO): void =>
              onPhotoClick(orderedPhotos, orderedPhotos.indexOf(photo))
            }
          />
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <Grid container={true} direction={'column'} spacing={3}>
      <Grid item={true}>
        <Divider />
      </Grid>
      {photos.length === 0 ? (
        <Grid item={true}>
          <Typography variant={'label1'} component={'p'} sx={{ color: (theme: Theme) => theme.colors.grayText }}>
            {'К письму не приложено ни одной фотографии.'}
          </Typography>
        </Grid>
      ) : (
        <Fragment>
          <Grid item={true}>
            <SectionCountLabel label={'ФОТОГРАФИИ ПИСЬМА'} count={`${photos.length} фото`} />
          </Grid>
          {deliveredPhotos.length > 0 && renderGroup(false, 'Доставлено', deliveredPhotos)}
          {declinedPhotos.length > 0 && renderGroup(true, 'Не прошли цензуру', declinedPhotos)}
        </Fragment>
      )}
    </Grid>
  );
};
